import type { VeeraContextualThreadEntrySurface, VeeraContextualThreadRole } from "@/lib/veeraContextualThreads";

export type VeeraThreadOpenerQuestionMode =
  | "action"
  | "understanding"
  | "processing"
  | "leader_coaching"
  | "coach_reflection"
  | "portfolio";

export type VeeraThreadOpenerEvidenceStrength = "thin" | "strong";

export interface VeeraThreadOpenerInput {
  role: VeeraContextualThreadRole;
  entrySurface: VeeraContextualThreadEntrySurface;
  sourceLabel: string;
  carryOverSummary: string;
  whyItMattersNow: string;
  evidenceStrength: VeeraThreadOpenerEvidenceStrength;
  question?: string | null;
  questionBuilder?: (params: {
    questionMode: VeeraThreadOpenerQuestionMode;
    role: VeeraContextualThreadRole;
    entrySurface: VeeraContextualThreadEntrySurface;
    sourceLabel: string;
    carryOverSummary: string;
    whyItMattersNow: string;
  }) => string;
}

export interface VeeraThreadOpenerResult {
  sourceLabel: string;
  structure: ["carry-over summary", "why it matters now", "question"];
  carryOverSummary: string;
  whyItMattersNow: string;
  questionMode: VeeraThreadOpenerQuestionMode;
  shouldAskQuestion: boolean;
  question: string | null;
}

function normalizeText(value: string | null | undefined) {
  const normalized = value?.replace(/\s+/g, " ").trim();
  return normalized ? normalized : null;
}

function resolveQuestionMode(
  role: VeeraContextualThreadRole,
  entrySurface: VeeraContextualThreadEntrySurface,
): VeeraThreadOpenerQuestionMode {
  if (role === "coach") {
    switch (entrySurface) {
      case "portfolio":
        return "portfolio";
      case "leader_card":
        return "leader_coaching";
      case "post_meeting_review":
        return "coach_reflection";
      default:
        return "leader_coaching";
    }
  }

  switch (entrySurface) {
    case "today":
      return "action";
    case "insights":
      return "understanding";
    case "reflection":
      return "processing";
    default:
      return "action";
  }
}

export function buildVeeraThreadOpener(params: VeeraThreadOpenerInput): VeeraThreadOpenerResult {
  const questionMode = resolveQuestionMode(params.role, params.entrySurface);
  const shouldAskQuestion = params.evidenceStrength === "strong";

  return {
    sourceLabel: normalizeText(params.sourceLabel) ?? "From Coach Veera",
    structure: ["carry-over summary", "why it matters now", "question"],
    carryOverSummary: normalizeText(params.carryOverSummary) ?? "",
    whyItMattersNow: normalizeText(params.whyItMattersNow) ?? "",
    questionMode,
    shouldAskQuestion,
    question: shouldAskQuestion
      ? params.questionBuilder?.({
        questionMode,
        role: params.role,
        entrySurface: params.entrySurface,
        sourceLabel: params.sourceLabel,
        carryOverSummary: params.carryOverSummary,
        whyItMattersNow: params.whyItMattersNow,
      }) ?? normalizeText(params.question)
      : null,
  };
}
